import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Copy, Landmark } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Alternative payment block shown alongside PayPal when admin enabled bank transfer.
 *
 * Props:
 * - amount: number for display
 * - currency: 'MXN' | 'USD'
 * - validate: () => bool       optional, called before creating order
 * - beforeCreate: async () => { referenceId, amount }  - creates the order (same pattern as PayPalCheckout)
 * - onConfirmed: ({ referenceId }) => void
 * - disabled: bool
 */
const TransferPaymentBlock = ({ amount, currency = 'MXN', validate, beforeCreate, onConfirmed, disabled }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [referenceId, setReferenceId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/payment-config`)
      .then((res) => {
        if (!cancelled) setConfig(res.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return null;
  if (!config?.transfer_enabled) return null;

  const handleOpenTransfer = async () => {
    if (validate && !validate()) return;
    if (beforeCreate) {
      setConfirming(true);
      try {
        const pre = await beforeCreate();
        if (pre?.referenceId) {
          setReferenceId(pre.referenceId);
        }
        setOpen(true);
      } catch (e) {
        toast.error('No se pudo registrar el pedido');
      } finally {
        setConfirming(false);
      }
    } else {
      setOpen(true);
    }
  };

  const handleCopyClabe = () => {
    if (config.transfer_bank_info) {
      navigator.clipboard.writeText(config.transfer_bank_info).catch(() => {});
      toast.success('Datos bancarios copiados');
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      toast.success('Pedido registrado. Recibirás un correo cuando confirmemos tu pago.');
      setOpen(false);
      if (onConfirmed) onConfirmed({ referenceId });
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
      <div className="text-center text-xs text-[#6B6660] my-3" data-testid="transfer-or-divider">— o —</div>
      <Button
        type="button"
        variant="outline"
        className="w-full border-[#0F2922] text-[#0F2922] rounded-xl hover:bg-[#0F2922]/5"
        onClick={handleOpenTransfer}
        disabled={disabled || confirming}
        data-testid="btn-pay-transfer"
      >
        {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Landmark className="w-4 h-4 mr-2" />}
        Pagar por transferencia bancaria
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="transfer-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#0F2922]">Pago por transferencia</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-[#FAF9F6] rounded-xl">
              <div className="text-xs uppercase tracking-wider text-[#6B6660]">Total a transferir</div>
              <div className="text-2xl font-bold text-[#0F2922] mt-1">
                ${Number(amount).toFixed(2)} {currency}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#0F2922]">Datos bancarios</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCopyClabe}
                  className="h-7 text-xs text-[#B85042]"
                  data-testid="btn-copy-bank-info"
                >
                  <Copy className="w-3 h-3 mr-1" />
                  Copiar
                </Button>
              </div>
              <pre className="text-sm bg-white border border-[#E5E3D8] rounded-xl p-3 whitespace-pre-wrap font-mono text-[#0F2922]">
                {config.transfer_bank_info}
              </pre>
            </div>

            <div className="text-xs text-[#6B6660]">
              Una vez hecha la transferencia, marca tu pedido como pendiente con el botón abajo.
              Te confirmaremos por correo en cuanto verifiquemos el pago.
            </div>

            <Button
              onClick={handleConfirm}
              disabled={confirming}
              className="w-full bg-[#B85042] hover:bg-[#B85042]/90 text-white rounded-xl"
              data-testid="btn-confirm-transfer"
            >
              {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Ya hice la transferencia
            </Button>

            {config.whatsapp_orders && (
              <a
                href={`https://wa.me/${config.whatsapp_orders.replace(/[^0-9]/g, '')}?text=Hola,%20hice%20una%20transferencia%20para%20mi%20pedido`}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center text-sm text-[#25D366] font-medium"
              >
                ¿Dudas? Escríbenos por WhatsApp
              </a>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TransferPaymentBlock;
